import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { BarChart } from "@mui/x-charts";
import React, { useEffect, useState } from "react";

import { useAppContext } from "../contexts/AppContextProvider";

const Analytics = () => {
  const {
    getActiveUsersByPartner,
    wabmActiveUsersByPartnerData,
    getRegistrationsByPartner,
    registrationsByPartner,
    getAllRechargesData,
    partnerInfo,
    allRechargesData,
  } = useAppContext();
  const [analyticsType, setAnalyticsType] = useState("monthly");
  const [activeGraph, setActiveGraph] = useState("customer-registered");

  const [range, setRange] = useState(12);

  const analyticsOptions = {
    monthly: [
      { label: "3 Months", value: 3 },
      { label: "6 Months", value: 6 },
      { label: "12 Months", value: 12 },
      { label: "24 Months", value: 24 },
      { label: "36 Months", value: 36 },
    ],

    weekly: [
      { label: "4 Weeks", value: 4 },
      { label: "13 Weeks", value: 13 },
      { label: "26 Weeks", value: 26 },
      { label: "52 Weeks", value: 52 },
    ],

    daily: [
      { label: "7 Days", value: 7 },
      { label: "15 Days", value: 15 },
      { label: "30 Days", value: 30 },
      { label: "45 Days", value: 45 },
      { label: "90 Days", value: 90 },
      { label: "180 Days", value: 180 },
    ],
  };

  const getAnalyticsData = (
    users,
    type,
    range,
    dateKey = "registrationDate",
  ) => {
    // MONTHLY
    if (type === "monthly") {
      const counts = {};

      users?.forEach((user) => {
        if (!user[dateKey]) return;

        const date = new Date(user[dateKey]);

        const key = `${date.getFullYear()}-${date.getMonth()}`;

        counts[key] = (counts[key] || 0) + 1;
      });

      const result = [];

      for (let i = range - 1; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);

        const key = `${date.getFullYear()}-${date.getMonth()}`;

        result.push({
          label: date.toLocaleString("en-US", {
            month: "short",
            year: "numeric",
          }),
          count: counts[key] || 0,
        });
      }

      return result;
    }

    // WEEKLY
    if (type === "weekly") {
      const counts = {};

      const getDateKey = (date) => {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(date.getDate()).padStart(2, "0")}`;
      };

      for (let i = range - 1; i >= 0; i--) {
        const startOfWeek = new Date();
        startOfWeek.setDate(
          startOfWeek.getDate() - startOfWeek.getDay() - i * 7,
        );

        startOfWeek.setHours(0, 0, 0, 0);

        counts[getDateKey(startOfWeek)] = 0;
      }

      // Count users
      users?.forEach((user) => {
        if (!user[dateKey]) return;
        const date = new Date(user[dateKey]);
        const startOfWeek = new Date(date);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
        startOfWeek.setHours(0, 0, 0, 0);
        const key = getDateKey(startOfWeek);
        if (counts.hasOwnProperty(key)) {
          counts[key]++;
        }
      });

      return Object.entries(counts).map(([key, count]) => {
        const [year, month, day] = key.split("-");
        const start = new Date(year, month - 1, day);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return {
          label: `${start.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
          })} - ${end.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
          })} ${end.getFullYear()}`,
          count,
        };
      });
    }

    // DAILY
    if (type === "daily") {
      const counts = {};

      users?.forEach((user) => {
        if (!user[dateKey]) return;

        const date = new Date(user[dateKey]);
        const key = date.toLocaleDateString("en-GB");

        counts[key] = (counts[key] || 0) + 1;
      });

      const result = [];

      for (let i = range - 1; i >= 0; i--) {
        const date = new Date();

        date.setDate(date.getDate() - i);

        const key = date.toLocaleDateString("en-GB");

        result.push({
          label: key,
          count: counts[key] || 0,
        });
      }

      return result;
    }
    return [];
  };

  const handleAnalyticsTypeChange = (type) => {
    setAnalyticsType(type);

    if (type === "monthly") {
      setRange(12);
    } else if (type === "weekly") {
      setRange(52);
    } else {
      setRange(45);
    }
  };

  const customerOnboaredData = getAnalyticsData(
    wabmActiveUsersByPartnerData,
    analyticsType,
    range,
  );

  const customerRegisteredData = getAnalyticsData(
    registrationsByPartner,
    analyticsType,
    range,
    "regDate",
  );

  const registersDoneData = getAnalyticsData(
    allRechargesData,
    analyticsType,
    range,
    "rechargeDate",
  );

  useEffect(() => {
    getActiveUsersByPartner();
    getRegistrationsByPartner();
    if (
      ["silver", "gold"].includes(
        String(
          partnerInfo?.partnerType ?? partnerInfo?.partner_type ?? "",
        ).toLowerCase(),
      )
    ) {
      getAllRechargesData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerInfo]);

  return (
    <main className="p-5 space-y-2">
      <div className="flex flex-wrap gap-3 justify-between">
        <div className=" mb-3">
          <h2 className="text-2xl font-semibold">Analytics</h2>
          <p className="mt-1">
            Track customer growth, engagement, and platform activity over time.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap justify-between ">
        <div className="inline-flex rounded-lg border border-borderColor overflow-hidden mb-4">
          <button
            type="button"
            onClick={() => setActiveGraph("customer-registered")}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-none  text-sm font-medium transition-all duration-150 ${
              activeGraph === "customer-registered"
                ? "bg-accent text-white"
                : "bg-backgroundPrimary text-textPrimary   hover:bg-backgroundSecondary"
            }`}
          >
            Customers Registered
          </button>
          <div className="w-px bg-borderColor" />
          <button
            type="button"
            onClick={() => setActiveGraph("customer-onboarded")}
            className={`flex items-center gap-2 rounded-none px-5 py-2.5 text-sm font-medium transition-all duration-150 ${
              activeGraph === "customer-onboarded"
                ? "bg-accent text-white"
                : "bg-backgroundPrimary text-textPrimary   hover:bg-backgroundSecondary"
            }`}
          >
            Customers Onboarded
          </button>
          <div className="w-px bg-borderColor" />
          <button
            type="button"
            onClick={() => setActiveGraph("recharges-done")}
            className={`flex items-center gap-2 rounded-none px-5 py-2.5 text-sm font-medium transition-all duration-150 ${
              activeGraph === "recharges-done"
                ? "bg-accent text-white"
                : "bg-backgroundPrimary text-textPrimary   hover:bg-backgroundSecondary"
            }`}
          >
            Recharges Done
          </button>
        </div>
        <div className="flex flex-col md:flex-row gap-1 md:gap-4">
          {/* Type Dropdown */}
          <div className="inline-flex rounded-lg border border-borderColor overflow-hidden mb-4">
            <button
              type="button"
              onClick={() => handleAnalyticsTypeChange("monthly")}
              className={`flex items-center gap-2 rounded-none px-5 py-2.5 text-sm font-medium transition-all duration-150 ${
                analyticsType === "monthly"
                  ? "bg-accent text-white"
                  : "bg-backgroundPrimary text-textPrimary   hover:bg-backgroundSecondary"
              }`}
            >
              Monthly
            </button>
            <div className="w-px bg-borderColor" />
            <button
              type="button"
              onClick={() => handleAnalyticsTypeChange("weekly")}
              className={`flex items-center gap-2 rounded-none px-5 py-2.5 text-sm font-medium transition-all duration-150 ${
                analyticsType === "weekly"
                  ? "bg-accent text-white"
                  : "bg-backgroundPrimary text-textPrimary   hover:bg-backgroundSecondary"
              }`}
            >
              Weekly
            </button>
            <div className="w-px bg-borderColor" />
            <button
              type="button"
              onClick={() => handleAnalyticsTypeChange("daily")}
              className={`flex items-center gap-2 rounded-none px-5 py-2.5 text-sm font-medium transition-all duration-150 ${
                analyticsType === "daily"
                  ? "bg-accent text-white"
                  : "bg-backgroundPrimary text-textPrimary   hover:bg-backgroundSecondary"
              }`}
            >
              Daily
            </button>
          </div>

          {/* Range Dropdown */}
          <FormControl size="small">
            <InputLabel
              sx={{
                color: "var(--text-secondary) !important",
                "&.Mui-focused": {
                  color: "var(--accent-color) !important",
                },
              }}
            >
              Range
            </InputLabel>

            <Select
              sx={{
                width: 180,
                color: "var(--text-primary)",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--border-color) !important",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "var(--text-secondary) !important",
                },
                "& .MuiSvgIcon-root": {
                  color: "var(--text-primary)",
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: "var(--background-primary)",
                    color: "var(--text-primary)",
                    border: "0.5px solid var(--border-color)",
                    "& .MuiMenuItem-root:hover": {
                      background: "var(--background-secondary)",
                    },
                    "& .MuiMenuItem-root.Mui-selected": {
                      background: "var(--background-secondary)",
                    },
                  },
                },
              }}
              value={range}
              label="Range"
              onChange={(e) => setRange(e.target.value)}
            >
              {analyticsOptions[analyticsType].map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6  ">
        {activeGraph === "customer-registered" && (
          <>
            <div className="">
              <h3 className="text-base font-semibold mb-1">
                Customers Registered
              </h3>
              <p>Breakdown of new customer signups over time</p>
            </div>

            {/* customer registered */}
            <div className="bg-backgroundPrimary p-0 md:p-3 rounded-lg shadow-md">
              <BarChart
                xAxis={[
                  {
                    scaleType: "band",
                    data: customerRegisteredData?.map((item) => item.label),
                  },
                ]}
                series={[
                  {
                    data: customerRegisteredData?.map((item) => item.count),
                    label: "Customers Registered",
                    color: "var(--accent-color)",
                  },
                ]}
                height={445}
                sx={{
                  "& .MuiChartsAxis-line": {
                    stroke: "var(--text-primary) !important",
                  },
                  "& .MuiChartsAxis-tick": {
                    stroke: "var(--text-primary) !important",
                  },
                  "& .MuiChartsAxis-tickLabel": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsLegend-label": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsLegend-series text": {
                    fill: "var(--text-primary) !important",
                  },
                  "& text": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsGrid-line": {
                    stroke: "var(--border-color) !important",
                  },
                  "& .mui-charts-tooltip-root": {
                    backgroundColor: "red",
                    color: "var(--text-primary) !important",
                    border: "1px solid var(--border-color)",
                  },

                  "& .MuiChartsTooltip-table": {
                    backgroundColor: "var(--background-primary) !important",
                    color: "var(--text-primary) !important",
                  },
                }}
              />
            </div>
          </>
        )}

        {/* customer Onboarded */}
        {activeGraph === "customer-onboarded" && (
          <>
            <div className="">
              <h3 className="text-base font-semibold mb-1">
                Customers Onboarded
              </h3>
              <p>Breakdown of customers successfully onboarded over time</p>
            </div>
            <div className="bg-backgroundPrimary p-0 md:p-3 rounded-lg shadow-md">
              <BarChart
                xAxis={[
                  {
                    scaleType: "band",
                    data: customerOnboaredData?.map((item) => item.label),
                  },
                ]}
                series={[
                  {
                    data: customerOnboaredData?.map((item) => item.count),
                    label: "Customers Onboarded",
                    color: "var(--accent-color)",
                  },
                ]}
                height={445}
                sx={{
                  "& .MuiChartsAxis-line": {
                    stroke: "var(--text-primary) !important",
                  },
                  "& .MuiChartsAxis-tick": {
                    stroke: "var(--text-primary) !important",
                  },
                  "& .MuiChartsAxis-tickLabel": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsLegend-label": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsLegend-series text": {
                    fill: "var(--text-primary) !important",
                  },
                  "& text": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsGrid-line": {
                    stroke: "var(--border-color) !important",
                  },
                }}
              />
            </div>
          </>
        )}

        {/* recharges done */}
        {activeGraph === "recharges-done" && (
          <>
            <div className="">
              <h3 className="text-base font-semibold mb-1 ">Recharge Done</h3>
              <p>Breakdown of recharges completed by customers over time</p>
            </div>
            <div className="bg-backgroundPrimary p-0 md:p-3 rounded-lg shadow-md">
              <BarChart
                xAxis={[
                  {
                    scaleType: "band",
                    data: registersDoneData?.map((item) => item.label),
                  },
                ]}
                series={[
                  {
                    data: registersDoneData?.map((item) => item.count),
                    label: "Recharges Done",
                    color: "var(--accent-color)",
                  },
                ]}
                height={445}
                sx={{
                  "& .MuiChartsAxis-line": {
                    stroke: "var(--text-primary) !important",
                  },
                  "& .MuiChartsAxis-tick": {
                    stroke: "var(--text-primary) !important",
                  },
                  "& .MuiChartsAxis-tickLabel": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsLegend-label": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsLegend-series text": {
                    fill: "var(--text-primary) !important",
                  },
                  "& text": {
                    fill: "var(--text-primary) !important",
                  },
                  "& .MuiChartsGrid-line": {
                    stroke: "var(--border-color) !important",
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default Analytics;
